import { createListCollection, HStack, Text } from '@chakra-ui/react';
import { CustomSelect, Option } from '@components/ui/select/Select';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { useSetManualTargetIdentificationMutation } from '@services/c2/track.api';
import { resolveIdentification } from '@utils/target.utils';
import { isEqual } from 'lodash';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TargetStatusEnum } from '@/types/c2/c2.types';
import { Identification, IdentificationEnum } from '@/types/sensor/identification.types';
import { TrackLight } from '@/types/sensor/sensors';

type Props = {
  identification: Identification;
  site: string;
  trackIds: TrackLight[];
  status: TargetStatusEnum;
  isDisabled?: boolean;
};

const AUTOMATIC = 'AUTOMATIC';

function IdentificationSelect({ identification, site, trackIds, status, isDisabled = false }: Readonly<Props>) {
  const formatMessage = useIntl().formatMessage;
  const [updateManualIdentification] = useSetManualTargetIdentificationMutation();
  const organization = useAppSelector(selectActiveOrganization);

  const manualIdentificationOptions = [
    {
      value: AUTOMATIC,
      label: formatMessage({
        id: `target.identification.${identification.autoIdentification}`,
      }),
    },
  ].concat(
    Object.keys(IdentificationEnum).map((key) => {
      return {
        value: key,
        label: formatMessage({ id: `target.identification.${key}` }),
      };
    }),
  );

  const computedIdentification = resolveIdentification(identification.manualIdentification, organization);

  const currentValue = organization && computedIdentification ? computedIdentification : AUTOMATIC;

  const formatLabel = (option: Option) => (
    <HStack gap={1} justifyContent="space-between" width="100%">
      <Text textStyle="screenStd" color="neutral.white">
        {option.label}
      </Text>
      <Text textStyle="screenStd" color="neutral.200">
        <FormattedMessage id={`target.${option.value === AUTOMATIC ? 'auto' : 'manual'}`} />
      </Text>
    </HStack>
  );

  return (
    <CustomSelect
      value={currentValue}
      collection={createListCollection({ items: manualIdentificationOptions })}
      disabled={isDisabled || status === TargetStatusEnum.LOST}
      width="100%"
      listWidth="100%"
      size="sm"
      formatLabel={formatLabel}
      onChange={(selectedOption) =>
        selectedOption &&
        updateManualIdentification({
          tracks: trackIds,
          site: site,
          identification: selectedOption === 'AUTOMATIC' ? null : (selectedOption as IdentificationEnum),
        })
      }
    />
  );
}

export default memo(IdentificationSelect, isEqual);
